import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import RuleIcon from '@mui/icons-material/Rule';
import { deletePolicy, getPolicies } from '../../../../api/endpoints/corporate';
import type { CorporatePolicy } from '../../../../api/types/corporate';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import {
  ListPageScaffold,
  RowActions,
  StatusChip,
  useDeleteResource,
  type DataTableColumn,
} from '../../../../components/common';
import { PolicyFormDialog } from '../PolicyFormDialog';

export default function PolicyListPage() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CorporatePolicy | null>(null);

  const query = useQuery({ queryKey: ['corporate', 'policies'], queryFn: () => getPolicies() });
  const rows: CorporatePolicy[] = query.data?.policies ?? [];

  const del = useDeleteResource<number>({
    mutationFn: (id) => deletePolicy(id),
    invalidateKeys: [['corporate', 'policies']],
    successMessage: 'Politika silindi.',
  });
  const deletingName = rows.find((r) => r.policyID === del.target)?.policyName ?? '';

  const columns: DataTableColumn<CorporatePolicy>[] = [
    { id: 'name', header: 'Politika Adı', cell: (r) => r.policyName },
    { id: 'flightBudget', header: 'Uçak Maks. Bütçe', cell: (r) => r.flightMaximumBudget ?? '—' },
    { id: 'hotelBudget', header: 'Otel Günlük Maks. Bütçe', cell: (r) => r.hotelDailyMaxBudget ?? '—' },
    {
      id: 'class',
      header: 'Sınıf İzni',
      cell: (r) => (
        <Stack direction="row" spacing={0.5}>
          {r.canUseBusinessClass && <Chip size="small" label="Business" />}
          {r.canUseFirstClass && <Chip size="small" label="First" />}
          {!r.canUseBusinessClass && !r.canUseFirstClass && (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      id: 'approval',
      header: 'Onay',
      cell: (r) => (
        <StatusChip
          tone={r.sendApproval ? 'pending' : 'neutral'}
          label={r.sendApproval ? 'Onaya Gönderilir' : 'Otomatik'}
        />
      ),
    },
  ];

  return (
    <ListPageScaffold
      title="Seyahat Kural Setleri (Politikalar)"
      createLabel="Yeni Politika"
      onCreate={() => setCreateOpen(true)}
      query={query}
      rows={rows}
      columns={columns}
      getRowKey={(r) => r.policyID}
      emptyTitle="Kayıtlı politika bulunamadı."
      rowActions={(r) => (
        <RowActions
          onEdit={() => setEditing(r)}
          onDelete={() => del.request(r.policyID)}
          leading={
            <Tooltip title="İstisnalar">
              <IconButton
                size="small"
                onClick={() => navigate(`/corporate/policy/${r.policyID}/exceptions`)}
                aria-label="İstisnalar"
              >
                <RuleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        />
      )}
    >
      <PolicyFormDialog open={createOpen} policy={null} onClose={() => setCreateOpen(false)} />
      <PolicyFormDialog open={!!editing} policy={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        {...del.confirmDialogProps}
        title="Politikayı sil"
        description={`"${deletingName}" politikasını silmek istediğinize emin misiniz?`}
      />
    </ListPageScaffold>
  );
}
